import React, { useRef, useState } from 'react';
import { Check, ChevronDown, Flag, Image as ImageIcon, Loader2, Paperclip, RefreshCw, X } from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
import { RegulationLink } from '../ui/RegulationLink';
import { ConfidenceTag } from '../ui/ConfidenceTag';
const artwork = 'https://cdn.magicpatterns.com/uploads/vdUWeg3pMiFWhf2nNPoBoU/Capture_d’ecran_2026-08-19_a_15.21.54.png';
const AO_URL = 'https://www.fda.gov.ph/wp-content/uploads/2021/03/Administrative-Order-No.-2014-0030.pdf';
const CIRCULAR_URL = 'https://www.fda.gov.ph/fda-circular-no-2023-009-adoption-of-2015-philippine-dietary-reference-intakes-pdri-percent-recommended-energy-intakes-recommended-nutrient-intakes-rei-rni-as-the-new-dietary-standard-of-all-pr/';
const completeness: {
  label: string;
  present: boolean;
}[] = [{
  label: 'servings per package',
  present: true
}, {
  label: 'serving size',
  present: true
}, {
  label: 'Energy',
  present: true
}, {
  label: 'Protein',
  present: true
}, {
  label: 'Fat, total',
  present: true
}, {
  label: 'saturated fat',
  present: true
}, {
  label: 'Carbohydrate',
  present: true
}, {
  label: 'Sugars',
  present: true
}, {
  label: 'Sodium',
  present: true
}, {
  label: 'Calcium',
  present: true
}, {
  label: 'Trans Fat',
  present: false
}, {
  label: 'Calories from Fat',
  present: false
}, {
  label: 'Cholesterol',
  present: false
}, {
  label: 'Dietary Fibre',
  present: false
}];
interface NutrientRow {
  label: string;
  indent?: boolean;
  /** Rendered before the dot-separated secondary figure, if any. */
  perServe: string;
  perServeSecondary?: string;
  per100: string;
  per100Secondary?: string;
  secondaryNote?: string;
  muted?: boolean;
  valueTooltip?: string;
  percent: string;
  percentMuted?: boolean;
  percentTooltip?: string;
  claimDriven?: boolean;
  /** Rows whose figure can be narrowed by dropping in a composition spec. */
  dropId?: DropId;
  dropLabel?: string;
  resolvedPerServe?: string;
  resolvedPer100?: string;
  resolvedPercent?: string;
  resolvedPercentTooltip?: string;
  extracted?: {
    field: string;
    value: string;
  }[];
}
type DropId = 'cholesterol' | 'fiber';
type DropStage = 'idle' | 'reading' | 'recalculating' | 'done';
const rows: NutrientRow[] = [{
  label: 'Calories (kcal) · Calories from Fat',
  perServe: '87',
  perServeSecondary: '33',
  per100: '289',
  per100Secondary: '112',
  secondaryNote: 'calculated from Total Fat — not yet printed',
  percent: '3% / 11%'
}, {
  label: 'Total Fat (g)',
  perServe: '3.7',
  per100: '12.4',
  percent: '4–9% / 15–29%'
}, {
  label: 'Saturated Fat (g)',
  indent: true,
  perServe: '2.2',
  per100: '7.2',
  percent: 'No PDRI reference',
  percentMuted: true
}, {
  label: 'Trans Fat (g)',
  indent: true,
  perServe: '—',
  per100: '—',
  muted: true,
  valueTooltip: "Cheese naturally contains trace trans fat (vaccenic acid, CLA) from ruminant milk fat — not from hydrogenated oils, which this formulation doesn't use. At this formulation's cheese content, the calculated amount rounds to 0g under standard labelling rounding rules.",
  percent: '—',
  percentMuted: true,
  percentTooltip: 'No PDRI 2015 reference exists for trans fat.'
}, {
  label: 'Cholesterol (mg)',
  dropId: 'cholesterol',
  dropLabel: 'Drop cheese spec to narrow',
  perServe: '—',
  per100: '—',
  muted: true,
  valueTooltip: "Calculated from standard cholesterol values for cheese (85–105mg/100g depending on variety) at this formulation's 79% cheese content. Everything else in the formulation is plant-based and contributes none.",
  resolvedPerServe: '23',
  resolvedPer100: '75',
  percent: '—',
  percentMuted: true,
  percentTooltip: 'PDRI 2015 sets no cholesterol reference, narrowed figure or not.',
  extracted: [{
    field: 'Document type',
    value: 'Ingredient Specification'
  }, {
    field: 'Ingredient',
    value: 'Cheddar-style natural cheese'
  }, {
    field: 'Cholesterol content',
    value: '95 mg/100g'
  }]
}, {
  label: 'Sodium (mg)',
  perServe: '202',
  per100: '673',
  percent: '10% / 34%'
}, {
  label: 'Total Carbohydrates (g)',
  perServe: '5.2',
  per100: '17.4',
  percent: '1% / 4–5%'
}, {
  label: 'Dietary Fiber (g)',
  indent: true,
  dropId: 'fiber',
  dropLabel: 'Drop cracker sub-formulation to complete',
  perServe: '—',
  per100: '—',
  muted: true,
  valueTooltip: "Floor only — calculated from the known Brown Rice component (48% of the cracker portion, ~3.5g fibre/100g). The remaining 52% — Potato Starch, Sunflower Oil, Flax Seeds, Black Sesame Seeds — isn't broken out individually yet. Flax and sesame both carry meaningfully more fibre than this floor assumes.",
  resolvedPerServe: '0.2',
  resolvedPer100: '0.8',
  percent: '—',
  percentMuted: true,
  percentTooltip: 'Not yet complete — a 25g/day fibre reference is established under FDA Circular 2023-009 §F.',
  resolvedPercent: '1% / 3%',
  resolvedPercentTooltip: 'Against the 25g/day fibre reference established under FDA Circular 2023-009 §F.',
  extracted: [{
    field: 'Document type',
    value: 'Cracker Sub-Formulation Breakdown'
  }, {
    field: 'Brown Rice',
    value: '48%'
  }, {
    field: 'Potato Starch',
    value: '30%'
  }, {
    field: 'Sunflower Oil',
    value: '12%'
  }, {
    field: 'Flax Seeds',
    value: '6%'
  }, {
    field: 'Black Sesame Seeds',
    value: '4%'
  }]
}, {
  label: 'Sugar (g)',
  indent: true,
  perServe: '<1.0',
  per100: '<3.3',
  percent: 'Not applicable',
  percentMuted: true,
  percentTooltip: 'Outside the adopted %REI/RNI scope'
}, {
  label: 'Total Protein (g)',
  perServe: '8.1',
  per100: '27.0',
  percent: '11% / 38%'
}];
const claimDrivenRow: NutrientRow = {
  label: 'Calcium (mg)',
  perServe: '198',
  per100: '659',
  percent: '26% / 88%',
  claimDriven: true,
  percentTooltip: "Not one of the 10 mandatory AO 2014-0030 nutrients — declared here because the 'SOURCE OF CALCIUM' claim requires it independently."
};
export function PhNipTab({
  onViewLabel
}: {
  onViewLabel?: () => void;
}) {
  const [showImage, setShowImage] = useState(false);
  const [dropStages, setDropStages] = useState<Record<DropId, DropStage>>({
    cholesterol: 'idle',
    fiber: 'idle'
  });
  const pendingDrop = useRef<DropId | null>(null);
  const dropInputRef = useRef<HTMLInputElement>(null);
  const requestDrop = (id: DropId) => {
    pendingDrop.current = id;
    dropInputRef.current?.click();
  };
  const runDrop = (id: DropId) => {
    setDropStages(stages => ({
      ...stages,
      [id]: 'reading'
    }));
    window.setTimeout(() => setDropStages(stages => ({
      ...stages,
      [id]: 'recalculating'
    })), 1000);
    window.setTimeout(() => setDropStages(stages => ({
      ...stages,
      [id]: 'done'
    })), 2000);
  };
  return <div className="space-y-5" data-id="element-2015" data-mp="3w0vsxj8k19zg">
      <input ref={dropInputRef} type="file" className="hidden" onChange={event => {
      if (event.target.files?.length && pendingDrop.current) runDrop(pendingDrop.current);
      pendingDrop.current = null;
      event.target.value = '';
    }} data-id="element-2016" data-mp="3w274444bxk0l" />
      <section className="rounded-2xl border border-[#dce3da] bg-white p-6 shadow-sm" aria-labelledby="ph-nip-heading" data-id="element-2017" data-mp="3g1n4fi2f6bd">
        <div className="flex flex-wrap items-center gap-3" data-id="element-2018" data-mp="cdu6pvutl04k">
          <h2 id="ph-nip-heading" className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-2019" data-mp="cggcpr22b7ko">
            Nutrition Information Panel
          </h2>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700 ring-1 ring-sky-200" data-id="element-2020" data-mp="mdcnk3ffo5se">
            <RefreshCw className="h-3 w-3" data-id="element-2021" data-mp="qbs7fyg7wy1l" />
            Standard NIP
          </span>
        </div>
        <p className="mt-1 text-sm text-[#6b7d70]" data-id="element-2022" data-mp="v77i6hixidts">Values extracted from the nutrition label.</p>
        <div className="mt-4 flex flex-wrap gap-2" data-id="element-2023" data-mp="1062e8l5wpyvu">
          <Pill data-id="element-2024" data-mp="1543uyzfv1m45">Serving: 30g</Pill>
          <Pill data-id="element-2025" data-mp="154vn20a911po">Servings/package: 4</Pill>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2026" data-mp="1a1cg98wb269m">
          <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-2027" data-mp="1vd2i7gfan1yq">NIP completeness</h3>
          <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 ring-1 ring-red-200" data-id="element-2028" data-mp="1qdzmiml7e83f">Incomplete</span>
        </div>
        <ul className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3" data-id="element-2029" data-mp="24ch8ofvgncj8">
          {completeness.map(item => <li key={item.label} className="flex items-center gap-2 text-sm text-[#56685c]" data-id="element-2030" data-mp="1zf7aaaq3fqj9">
              {item.present ? <Check className="h-4 w-4 shrink-0 text-forest-green" data-id="element-2031" data-mp="1bpcvvoq4ltww" /> : <X className="h-4 w-4 shrink-0 text-red-600" data-id="element-2032" data-mp="17oaw96sarfue" />}
              <span className={item.present ? undefined : 'font-semibold text-red-700'} data-id="element-2033" data-mp="17lopvah576dr">{item.label}</span>
            </li>)}
        </ul>
        <p className="mt-6 rounded-xl bg-[#f5f8f3] px-4 py-3 text-sm leading-relaxed text-[#68796c]" data-id="element-2034" data-mp="1gnxy1toxunnz">
          No added vitamin or mineral ingredients are declared on this panel — the nutrient profile here is naturally occurring (calcium from
          cheese), so there&apos;s nothing to run a permissibility check against.
        </p>
      </section>

      <section className="rounded-2xl border border-[#dce3da] bg-white p-6 shadow-sm" aria-labelledby="ph-nutrition-table" data-id="element-2035" data-mp="2zkrj3bagzk3j">
        <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-2036" data-mp="2qmftnclcasco">
          <div className="min-w-0" data-id="element-2037" data-mp="2qkcuo327lovt">
            <h2 id="ph-nutrition-table" className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-2038" data-mp="2vkr1jhs2qss9">
              Nutrition table (AO 2014-0030 structure)
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-[#6b7d70]" data-id="element-2039" data-mp="3ncgdqyarkknn">
              Values shown as declared on the current artwork, in the naming and order this order requires. Nutrients not yet declared are flagged,
              not estimated.
            </p>
          </div>
          <button type="button" onClick={() => setShowImage(open => !open)} className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-[#d6dfd3] bg-white px-3.5 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-2040" data-mp="3edv11jrz3zus">
            <ImageIcon className="h-4 w-4" data-id="element-2041" data-mp="gbd6jkya08gi" />
            {showImage ? 'Hide label image' : 'Compare with label image'}
          </button>
        </div>

        {showImage && <div className="mt-4" data-id="element-2042" data-mp="6ltm4aux06jj">
            <p className="text-xs text-[#8b968d]" data-id="element-2043" data-mp="6mlhploii49t">Source: label artwork, v5 proof.</p>
            <img src={artwork} alt="Cheese Snack nutrition panel artwork" className="mt-2 w-full max-w-sm rounded-xl border border-[#e1e8df] bg-white object-contain" data-id="element-2044" data-mp="bkd82b9idibw" />
          </div>}

        <div className="mt-5 overflow-x-auto" data-id="element-2045" data-mp="1mqrzlgh3kqc">
          <table className="w-full min-w-[38rem] border-collapse text-sm" data-id="element-2046" data-mp="1nib3wsvvpzq">
            <thead data-id="element-2047" data-mp="3jt54dfz3fmmm">
              <tr className="border-b border-[#e1e8df] text-left font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#7a897d]" data-id="element-2048" data-mp="3jtn78vg9gz3i">
                <th scope="col" className="py-2 pr-4" data-id="element-2049" data-mp="1i0z22gk241l3">
                  Nutrient
                </th>
                <th scope="col" className="py-2 pr-4 text-right" data-id="element-2050" data-mp="1n01tuzb10bb4">
                  Per 30g
                </th>
                <th scope="col" className="py-2 pr-4 text-right" data-id="element-2051" data-mp="37hkuaho0vy3y">
                  Per 100g
                </th>
                <th scope="col" className="py-2 text-right" data-id="element-2052" data-mp="32jstsz9bb3o5">
                  %REI/RNI
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#edf0eb]" data-id="element-2053" data-mp="3ha0bhcrhwins">
              {rows.map(row => <NutrientTableRow key={row.label} row={row} stage={row.dropId ? dropStages[row.dropId] : 'idle'} onDrop={row.dropId ? () => requestDrop(row.dropId as DropId) : undefined} data-id="element-2054" data-mp="3cfmcz1hjdvew" />)}
            </tbody>
            <tbody className="border-t-2 border-[#dce3da]" data-id="element-2055" data-mp="3l8zhl0oyqlgt">
              <NutrientTableRow row={claimDrivenRow} stage="idle" data-id="element-2056" data-mp="41niy5e4kk41" />
            </tbody>
          </table>
        </div>

        <div className="mt-4 space-y-1.5 text-xs leading-relaxed text-[#8b968d]" data-id="element-2057" data-mp="3v901anqqv3lj">
          <p data-id="element-2058" data-mp="25ryz5n65m6zw">
            %REI/RNI computed against the PDRI 2015 reference for male adults 19–29 — <span className="font-semibold text-red-700" data-id="element-2059" data-mp="20u75anly8at2">2,530</span>{' '}
            kcal/day.{' '}
            <Tooltip content="Your source template cites 2,490 kcal — confirm which figure your regulatory contact expects before filing." data-id="element-2060" data-mp="2qib65kzioha8">
              <span className="inline-flex cursor-help align-middle text-amber-600" data-id="element-2061" data-mp="2ujwq1qagxvtx">
                <Flag className="h-3.5 w-3.5" data-id="element-2062" data-mp="2zhpyyr35p3xw" />
              </span>
            </Tooltip>
          </p>
          <p data-id="element-2063" data-mp="34idpzcrdxks4">Fat and Carbohydrate are shown as ranges because PDRI sets them as a percentage-of-energy interval, not a single reference value.</p>
          <p data-id="element-2064" data-mp="3di91jiourhso">Sugars declared as &apos;Less than 1.0g&apos; — not an integer, see Packaging &amp; Label finding.</p>
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-id="element-2065" data-mp="3i8phpak25hiq">
          <RegulationLink text="AO 2014-0030" href={AO_URL} data-id="element-2066" data-mp="3ib25i4chym3p" />
          <RegulationLink text="FDA Circular 2023-009" href={CIRCULAR_URL} data-id="element-2067" data-mp="3n6qzls5qzasm" />
        </div>
      </section>
    </div>;
}
function NutrientTableRow({
  row,
  stage,
  onDrop
}: {
  row: NutrientRow;
  stage: DropStage;
  onDrop?: () => void;
}) {
  const [showExtracted, setShowExtracted] = useState(false);
  const resolved = stage === 'done';
  const processing = stage === 'reading' || stage === 'recalculating';
  const value = (primary: string, secondary?: string) => {
    const body = <span className={`font-mono ${row.muted ? 'text-[#a3aea5]' : 'text-[#56685c]'}`} data-id="element-2068" data-mp="1bjxb8g5d59cb">
        {primary}
        {secondary && <>
            <span className="text-[#b3bdb4]" data-id="element-2069" data-mp="16ngjlbyygcuh"> · </span>
            <span className="font-normal text-[#9aa79c]" data-id="element-2070" data-mp="249nnejnfq15s">{secondary}</span>
          </>}
      </span>;
    if (!row.valueTooltip) return body;
    return <Tooltip content={row.valueTooltip} data-id="element-2071" data-mp="1zb2wjau2otu0">
        <span className="cursor-help underline decoration-dotted underline-offset-2" data-id="element-2072" data-mp="1uj2rx5325cr3">{body}</span>
      </Tooltip>;
  };
  const percentText = resolved && row.resolvedPercent ? row.resolvedPercent : row.percent;
  const percentTooltip = resolved && row.resolvedPercentTooltip ? row.resolvedPercentTooltip : row.percentTooltip;
  const percentMuted = row.percentMuted && !(resolved && row.resolvedPercent);
  const percent = <span className={`font-mono ${percentMuted ? 'text-[#a3aea5]' : 'font-semibold text-evergreen'}`} data-id="element-2073" data-mp="22q47x8diok1u">{percentText}</span>;
  const perServe = resolved && row.resolvedPerServe ? row.resolvedPerServe : row.perServe;
  const per100 = resolved && row.resolvedPer100 ? row.resolvedPer100 : row.per100;
  return <>
      <tr data-id="element-2074" data-mp="1xsubh8rpn84i">
        <th scope="row" className={`py-2.5 pr-4 text-left font-medium text-evergreen ${row.indent ? 'pl-5' : ''}`} data-id="element-2075" data-mp="1xsks44167z7s">
          <span className="flex flex-wrap items-center gap-2" data-id="element-2076" data-mp="2ljq8qlexqvj5">{row.label}</span>
          {row.secondaryNote && <span className="mt-1 block text-[11px] font-normal text-[#9aa79c]" data-id="element-2077" data-mp="2qit75n0eu3i5">({row.secondaryNote})</span>}
          {row.dropId && <span className="mt-1 block" data-id="element-2078" data-mp="2gqne09yf9g1d">
              {processing ? <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-700" data-id="element-2079" data-mp="2gpc6q05rpq5i">
                  <Loader2 className="h-3 w-3 animate-spin" data-id="element-2080" data-mp="34f6isfi9jaop" />
                  {stage === 'reading' ? 'Reading spec…' : 'Recalculating…'}
                </span> : resolved ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-forest-green" data-id="element-2081" data-mp="xrh2fqt2h0x5">
                  <Check className="h-3 w-3" data-id="element-2082" data-mp="ss4ef2qglnsz" />
                  Narrowed from spec
                </span> : null}
            </span>}
        </th>
        <td className="py-2.5 pr-4 text-right align-top" data-id="element-2083" data-mp="17jv7asffynnj">{value(perServe, row.perServeSecondary)}</td>
        <td className="py-2.5 pr-4 text-right align-top" data-id="element-2084" data-mp="12lbkyqwxr9un">{value(per100, row.per100Secondary)}</td>
        <td className="py-2.5 text-right align-top" data-id="element-2085" data-mp="ev7s9elacuif">
          {percentTooltip ? <Tooltip content={percentTooltip} data-id="element-2086" data-mp="9vuva10pa724">
              <span className="cursor-help underline decoration-dotted underline-offset-2" data-id="element-2087" data-mp="ntt995b401h3">{percent}</span>
            </Tooltip> : percent}
        </td>
      </tr>
      {resolved && row.extracted && <tr data-id="element-2088" data-mp="1z9815uogo35c">
          <td colSpan={4} className={`pb-3 ${row.indent ? 'pl-5' : ''}`} data-id="element-2089" data-mp="1z8owhfntnb99">
            <button type="button" onClick={() => setShowExtracted(open => !open)} aria-expanded={showExtracted} className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#7a897d] transition hover:text-evergreen" data-id="element-2090" data-mp="1zb2qdl6rmhf8">
              <ChevronDown className={`h-3 w-3 transition ${showExtracted ? 'rotate-180' : ''}`} data-id="element-2091" data-mp="wu8vxndranxo" />
              Extracted data
            </button>
            {showExtracted && <ul className="mt-2 divide-y divide-[#edf0eb] rounded-lg border border-[#e1e8df] bg-[#fbfdf9]" data-id="element-2092" data-mp="15wrye6dmqyje">
                {row.extracted.map(item => <li key={item.field} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2" data-id="element-2093" data-mp="982fccq9nzvu">
                    <span className="min-w-[9rem] font-mono text-[10px] font-semibold uppercase tracking-[0.11em] text-[#7a897d]" data-id="element-2094" data-mp="e37yj3sab0g8">{item.field}</span>
                    <span className="flex-1 text-xs text-evergreen" data-id="element-2095" data-mp="mxw3stxv0i51">{item.value}</span>
                    <ConfidenceTag level="HIGH" data-id="element-2096" data-mp="3mldxry076qbe" />
                  </li>)}
              </ul>}
          </td>
        </tr>}
    </>;
}
function Pill({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="rounded-full border border-[#d6dfd3] px-3 py-1 text-xs font-semibold text-[#5f7266]" data-id="element-2097" data-mp="3j2y80ly3pk9f">{children}</span>;
}